(function() {
  'use strict';

  if (window.DateSliderShared) return;

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const parseISODate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const normalized = dateString.trim();
    if (!normalized) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
      const parsed = new Date(`${normalized}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(normalized)) {
      const parts = normalized.split('/');
      if (parts.length === 3) {
        const parsed = new Date(parseInt(parts[2], 10), parseInt(parts[0], 10) - 1, parseInt(parts[1], 10));
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }
    }
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateLabel = (isoString) => {
    const parsed = parseISODate(isoString);
    if (!parsed) return isoString || '';
    try {
      return parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (e) {
      return isoString;
    }
  };

  const normalizeToDateOnly = (date) => {
    if (!date || !(date instanceof Date)) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  };

  // Remove a fixed set of inline style properties from an element
  const clearInlineStyles = (el) => {
    if (!el) return;
    ['opacity', 'filter', 'pointer-events', 'cursor', 'display', 'visibility'].forEach(p => el.style.removeProperty(p));
  };

  // ─── Card state helpers ──────────────────────────────────────────────────────

  // Returns the grid item and card wrapper ancestors (deduplicated from card itself)
  const getAncestors = (card) => {
    const gridItem = card.closest('.grid__item, li.grid__item, .collection__item, .product-grid__item');
    const cardWrapper = card.closest('.product-card-wrapper, .card-wrapper');
    return {
      gridItem: gridItem !== card ? gridItem : null,
      cardWrapper: cardWrapper !== card ? cardWrapper : null,
    };
  };

  const showCard = (card) => {
    if (!card) return;
    clearInlineStyles(card);
    card.classList.remove('date-slider-hidden', 'date-slider-disabled');
    card.classList.add('date-slider-visible', 'date-slider-enabled');
    const { gridItem, cardWrapper } = getAncestors(card);
    [gridItem, cardWrapper].forEach(el => {
      if (!el) return;
      clearInlineStyles(el);
      el.classList.remove('date-slider-hidden', 'date-slider-disabled');
      el.classList.add('date-slider-visible', 'date-slider-enabled');
    });
  };

  const hideCard = (card) => {
    if (!card) return;
    if (card.closest('section[id^="MainProduct"], #MainProduct') || card.matches('section[id^="MainProduct"], #MainProduct')) return;
    card.classList.add('date-slider-disabled');
    card.classList.remove('date-slider-visible', 'date-slider-enabled');
    const { gridItem, cardWrapper } = getAncestors(card);
    [gridItem, cardWrapper].forEach(el => {
      if (!el) return;
      el.classList.add('date-slider-disabled');
      el.classList.remove('date-slider-visible', 'date-slider-enabled');
    });
  };

  const addMatchBadge = (card, text = 'Available') => {
    if (!card) return;
    if (!text) { removeMatchBadge(card); return; }
    card.classList.add('date-slider-badged');
    let badge = card.querySelector('[data-date-slider-badge]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'date-match-badge';
      badge.setAttribute('data-date-slider-badge', 'true');
      badge.setAttribute('aria-hidden', 'true');
      card.appendChild(badge);
    }
    badge.textContent = text;
  };

  const removeMatchBadge = (card) => {
    if (!card) return;
    card.classList.remove('date-slider-badged');
    card.querySelector('[data-date-slider-badge]')?.remove();
  };

  const addDisabledState = (card) => {
    if (!card) return;
    card.classList.add('date-slider-disabled');
    card.classList.remove('date-slider-enabled');
    card.style.opacity = '0.5';
    card.style.pointerEvents = 'none';
    card.querySelectorAll('.quick-add, .quick-add__submit, [class*="quick-add"], .quick-add-modal, [id*="QuickAdd"], [id*="quick-add"]').forEach(el => {
      el.style.display = 'none';
      el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
    });
    card.querySelectorAll('button, a[href], input[type="submit"], [role="button"]').forEach(btn => {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('date-slider-disabled');
      btn.classList.remove('date-slider-enabled');
    });
  };

  const removeDisabledState = (card) => {
    if (!card) return;
    card.classList.remove('date-slider-disabled', 'date-slider-hidden');
    card.classList.add('date-slider-enabled', 'date-slider-visible');
    clearInlineStyles(card);

    const mainProductSection = card.closest('section[id^="MainProduct"], #MainProduct') ||
                               (card.matches('section[id^="MainProduct"], #MainProduct') ? card : null);

    if (mainProductSection) {
      [mainProductSection, ...mainProductSection.querySelectorAll('*')].forEach(el => {
        el.classList.remove('date-slider-disabled', 'date-slider-hidden');
        el.classList.add('date-slider-enabled', 'date-slider-visible');
        clearInlineStyles(el);
      });
      mainProductSection.querySelectorAll('button, .quick-add, form, [class*="quick-add"], [class*="add-to-cart"]').forEach(btn => {
        btn.removeAttribute('disabled');
        btn.classList.remove('date-slider-disabled');
      });
      return;
    }

    const { gridItem, cardWrapper } = getAncestors(card);
    [gridItem, cardWrapper].forEach(el => {
      if (!el) return;
      el.classList.remove('date-slider-disabled', 'date-slider-hidden');
      el.classList.add('date-slider-enabled', 'date-slider-visible');
      clearInlineStyles(el);
    });

    card.querySelectorAll('.quick-add, .quick-add__submit, [class*="quick-add"], .quick-add-modal, [id*="QuickAdd"], [id*="quick-add"]').forEach(el => clearInlineStyles(el));
    card.querySelectorAll('button, a[href], input[type="submit"], [role="button"]').forEach(btn => {
      btn.removeAttribute('disabled');
      btn.classList.remove('date-slider-disabled');
      clearInlineStyles(btn);
    });
    card.querySelectorAll('img').forEach(img => { img.style.removeProperty('filter'); img.style.removeProperty('opacity'); });
  };

  // ─── Card collection ─────────────────────────────────────────────────────────

  const SKIP_SELECTORS = [
    'footer, .site-footer, #shopify-section-footer, [id*="footer"]',
    '.collection-card-wrapper, .card-collection',
    '.multicolumn-list, .multicolumn-card, [class*="multicolumn"]',
    '.related-products, product-recommendations, [class*="related-product"], [class*="complementary-product"]',
  ];

  const shouldSkipCard = (card) => SKIP_SELECTORS.some(sel => card.closest(sel) || card.matches(sel));

  const collectCards = ({ scope, selectors = [], requireAvailabilityAttr = false, container = null, excludeElement = null } = {}) => {
    const root = container instanceof Element ? container : (scope instanceof Element ? scope : document);
    const defaultSelectors = ['[data-product-handle]', '.product-card-wrapper', '.product-card', '.grid-product', '.collection__item', '.related-product', '.recommended-product'];
    const unique = new Set();

    (selectors.length ? selectors : defaultSelectors).forEach((selector) => {
      root.querySelectorAll(selector).forEach((card) => {
        if (!(card instanceof Element)) return;
        if (excludeElement && (excludeElement.contains(card) || card === excludeElement)) return;
        if (shouldSkipCard(card)) return;

        let elementToUse = card;
        if (!card.hasAttribute('data-available-from') && !card.hasAttribute('data-available-to')) {
          const wrapper = card.querySelector('.card-wrapper.product-card-wrapper, .product-card-wrapper');
          if (wrapper && (wrapper.hasAttribute('data-available-from') || wrapper.hasAttribute('data-available-to'))) {
            elementToUse = wrapper;
          }
        }

        if (requireAvailabilityAttr && !elementToUse.hasAttribute('data-available-from') && !elementToUse.hasAttribute('data-available-to')) return;
        unique.add(elementToUse);
      });
    });

    return Array.from(unique);
  };

  // ─── Date availability ───────────────────────────────────────────────────────

  const getDateAttrs = (card) => {
    let from = card.getAttribute('data-available-from');
    let to = card.getAttribute('data-available-to');
    if (!from && !to) {
      const gridItem = card.closest('.grid__item, li.grid__item, .collection__item, .product-grid__item');
      if (gridItem) { from = gridItem.getAttribute('data-available-from'); to = gridItem.getAttribute('data-available-to'); }
    }
    if (!from && !to) {
      const wrapper = card.querySelector('.card-wrapper.product-card-wrapper, .product-card-wrapper, .card-wrapper');
      if (wrapper) { from = wrapper.getAttribute('data-available-from'); to = wrapper.getAttribute('data-available-to'); }
    }
    return { from, to };
  };

  const isCardAvailableForDate = (card, comparisonDate, availableFromOverride = null, availableToOverride = null) => {
    if (!card || !comparisonDate) return false;
    const from = availableFromOverride !== null ? availableFromOverride : getDateAttrs(card).from;
    const to = availableToOverride !== null ? availableToOverride : getDateAttrs(card).to;
    if (!from && !to) return false;

    const normalized = normalizeToDateOnly(comparisonDate);
    if (!normalized) return false;
    if (from) {
      const f = normalizeToDateOnly(parseISODate(from));
      if (f && normalized < f) return false;
    }
    if (to) {
      const t = normalizeToDateOnly(parseISODate(to));
      if (t && normalized > t) return false;
    }
    return true;
  };

  // ─── Filter cards ────────────────────────────────────────────────────────────

  const enableCard = (card, badgeText) => {
    showCard(card);
    if (badgeText) addMatchBadge(card, badgeText); else removeMatchBadge(card);
    removeDisabledState(card);
  };

  const filterCards = ({ cards = [], isoDate, badgeText = 'Available', onMatch, onMismatch, showCardsWithoutDatesAsEnabled = false } = {}) => {
    if (!isoDate) {
      cards.forEach((card) => {
        enableCard(card, null);
        if (typeof onMatch === 'function') onMatch(card, true);
      });
      return { visibleCount: cards.length, totalCount: cards.length };
    }

    const comparisonDate = parseISODate(isoDate);
    if (!comparisonDate) {
      console.warn('DateSliderShared: unable to parse date', isoDate);
      return { visibleCount: 0, totalCount: cards.length };
    }

    const isHomepage = window.location.pathname === '/' ||
                       document.body.classList.contains('template-index') ||
                       window.PAGE_TYPE === 'index';

    let visibleCount = 0;
    const matchingCards = [], outOfRangeCards = [], cardsWithoutDates = [];

    cards.forEach((card) => {
      // Resolve date attrs — may redirect to a child wrapper
      let { from, to } = getDateAttrs(card);
      let targetCard = card;
      if (!from && !to) {
        const wrapper = card.querySelector('.card-wrapper.product-card-wrapper, .product-card-wrapper, .card-wrapper');
        if (wrapper) { const a = getDateAttrs(wrapper); if (a.from || a.to) { from = a.from; to = a.to; targetCard = wrapper; } }
      }

      if (!from && !to) {
        cardsWithoutDates.push(card);
      } else if (isCardAvailableForDate(targetCard, comparisonDate, from, to)) {
        matchingCards.push(targetCard);
      } else {
        outOfRangeCards.push(targetCard);
      }
    });

    matchingCards.forEach((card) => {
      visibleCount++;
      enableCard(card, badgeText);
      if (typeof onMatch === 'function') onMatch(card, true);
    });

    outOfRangeCards.forEach((card) => {
      showCard(card);
      removeMatchBadge(card);
      addDisabledState(card);
      if (typeof onMismatch === 'function') onMismatch(card, false);
    });

    cardsWithoutDates.forEach((card) => {
      if (shouldSkipCard(card) || showCardsWithoutDatesAsEnabled || isHomepage) {
        visibleCount++;
        enableCard(card, null);
      } else if (isoDate && comparisonDate) {
        showCard(card);
        addDisabledState(card);
      } else {
        visibleCount++;
        enableCard(card, null);
      }
      if (typeof onMatch === 'function') onMatch(card, false);
    });

    // Reorder: matching → out-of-range → no-dates within each parent
    const parentGroups = new Map();
    [...matchingCards, ...outOfRangeCards, ...cardsWithoutDates].forEach((card) => {
      const el = card.matches('.grid__item, li.grid__item, .collection__item, .product-grid__item')
        ? card
        : (card.closest('.grid__item, li.grid__item, .collection__item, .product-grid__item') || card);
      const parent = el.parentElement;
      if (!parent) return;
      if (!parentGroups.has(parent)) parentGroups.set(parent, { matching: [], outOfRange: [], noDates: [] });
      const g = parentGroups.get(parent);
      if (matchingCards.includes(card)) g.matching.push(el);
      else if (outOfRangeCards.includes(card)) g.outOfRange.push(el);
      else g.noDates.push(el);
    });

    parentGroups.forEach((g, parent) => {
      const ordered = [...g.matching, ...g.outOfRange, ...g.noDates].filter(el => el.parentElement === parent);
      if (ordered.length < 2) return;
      const insertBefore = (() => {
        let node = ordered[0].nextSibling;
        while (node && ordered.includes(node)) node = node.nextSibling;
        return node;
      })();
      const frag = document.createDocumentFragment();
      ordered.forEach(el => { if (el.parentElement === parent) { parent.removeChild(el); frag.appendChild(el); } });
      insertBefore && insertBefore.parentElement === parent ? parent.insertBefore(frag, insertBefore) : parent.appendChild(frag);
    });

    return { visibleCount, totalCount: cards.length };
  };

  // ─── Utilities ───────────────────────────────────────────────────────────────

  const countMatchesForDates = (cards = [], isoDates = []) => {
    const result = {};
    isoDates.forEach((iso) => {
      const date = parseISODate(iso);
      result[iso] = date ? cards.filter(card => {
        const { from, to } = getDateAttrs(card);
        return (from || to) && isCardAvailableForDate(card, date);
      }).length : 0;
    });
    return result;
  };

  const updateStatus = ({ element, isoDate, visibleCount, totalCount }) => {
    if (!element) return;
    let message = '';
    if (!totalCount) message = 'No products with delivery windows to filter yet.';
    else if (!isoDate) message = 'Select a delivery date to filter available products.';
    else if (!visibleCount) message = `No products available for ${formatDateLabel(isoDate)}.`;
    else message = `Showing ${visibleCount} product${visibleCount === 1 ? '' : 's'} for <strong>${formatDateLabel(isoDate)}</strong>.`;
    element.innerHTML = message;
  };

  // ─── Export ──────────────────────────────────────────────────────────────────

  window.DateSliderShared = {
    parseISODate,
    formatDateLabel,
    collectCards,
    showCard,
    hideCard,
    addMatchBadge,
    removeMatchBadge,
    addDisabledState,
    removeDisabledState,
    isCardAvailableForDate,
    filterCards,
    countMatchesForDates,
    updateStatus,
    isBlackoutDate(isoDate) {
      if (!isoDate) return false;
      return (window.blackoutDates || []).includes(String(isoDate).slice(0, 10));
    }
  };
})();
